// 登入頁

import { useEffect, useRef, useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";

// Google 登入：與拾光 ERP／官網共用同一組 OAuth 用戶端（client id 是公開值），伺服器端只認綁定的信箱
const GOOGLE_CLIENT_ID =
  "44185792640-fuo9hbvputpgpdqae3v764lhim0dcl19.apps.googleusercontent.com";

type GoogleId = {
  initialize: (o: { client_id: string; callback: (r: { credential: string }) => void }) => void;
  renderButton: (el: HTMLElement, o: Record<string, unknown>) => void;
};

function loadGis(): Promise<GoogleId | null> {
  const w = window as unknown as { google?: { accounts?: { id?: GoogleId } } };
  if (w.google?.accounts?.id) return Promise.resolve(w.google.accounts.id);
  return new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.onload = () => resolve(w.google?.accounts?.id ?? null);
    s.onerror = () => resolve(null);
    document.head.appendChild(s);
  });
}

export default function LoginPage() {
  const { user, loading, login, loginWithGoogle } = useAuth();
  const googleRef = useRef<HTMLDivElement>(null);
  const [googleReady, setGoogleReady] = useState<"loading" | "ok" | "fail">("loading");
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loading || user) return;
    let cancelled = false;
    void loadGis().then((gid) => {
      if (cancelled) return;
      if (!gid || !googleRef.current) {
        setGoogleReady("fail");
        return;
      }
      gid.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (r) => {
          setError(null);
          setSubmitting(true);
          try {
            await loginWithGoogle(r.credential);
          } catch (err) {
            setError((err as Error).message);
          } finally {
            setSubmitting(false);
          }
        },
      });
      gid.renderButton(googleRef.current, {
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        locale: "zh-TW",
        width: Math.min(googleRef.current.offsetWidth || 320, 400),
      });
      setGoogleReady("ok");
    });
    return () => {
      cancelled = true;
    };
  }, [loading, user, loginWithGoogle]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-brand-dark/60 text-sm">
        載入中⋯⋯
      </div>
    );
  }
  if (user) {
    const from = (location.state as { from?: string } | null)?.from ?? "/";
    return <Navigate to={from} replace />;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username, password);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="font-serif text-3xl font-bold text-brand-dark mb-2">
            麥開會
          </div>
          <div className="uppercase tracking-[0.4em] text-xs text-brand-gold font-medium">
            ChillMeet
          </div>
          <div className="text-xs text-brand-dark/55 mt-3 font-light tracking-wide">
            會議講究，但不將就時間
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-lg shadow-sm border border-brand-gray/15 p-7 space-y-5"
        >
          <h1 className="font-serif text-lg font-bold text-brand-dark">
            你回來了，先登入
          </h1>

          <div>
            <div ref={googleRef} className="w-full min-h-[44px] flex justify-center" />
            {googleReady === "fail" && (
              <p className="text-xs text-brand-dark/50 text-center">
                Google 登入載入不了，先用帳號密碼登入
              </p>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-brand-dark/45">
            <span className="h-px flex-1 bg-brand-gray/25" />
            或用帳號密碼
            <span className="h-px flex-1 bg-brand-gray/25" />
          </div>

          <div>
            <label className="block text-xs font-medium text-brand-dark/70 mb-1.5">
              帳號
            </label>
            <input
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-3 py-2.5 border border-brand-gray/30 rounded-md text-sm focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-brand-dark/70 mb-1.5">
              密碼
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2.5 border border-brand-gray/30 rounded-md text-sm focus:outline-none focus:border-brand-gold focus:ring-1 focus:ring-brand-gold/30"
              required
            />
          </div>

          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-brand-dark text-white py-2.5 rounded-md text-sm font-medium hover:bg-brand-dark/90 disabled:opacity-50 transition-colors"
          >
            {submitting ? "登入中⋯⋯" : "登入"}
          </button>

          <div className="text-center text-xs text-brand-dark/50 pt-1">
            麥閒系列工具 · 拾光快門出品
          </div>
        </form>
      </div>
    </div>
  );
}
